import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import { createBackgroundJob } from "@/api/background-job/createBackgroundJob";
import { getBackgroundJobList } from "@/api/background-job/getBackgroundJobList";
import { isBackgroundJobActive } from "@/api/background-job/_models/BackgroundJob";
import type { BackgroundJobStatus } from "@/api/background-job/_models/BackgroundJob";
import { formatDateTime } from "@/app/date.utils";
import { SnapshotPipelineStep } from "@/components/snapshot/SnapshotPipelineStep";
import { toErrorText } from "@/components/snapshot/snapshot.utils";
import { useSnapshot } from "@/components/snapshot/snapshot.context";
import { ORDER_SNAPSHOT_STATUS_IMPORT_KIND, toOrderSnapshotStatusImportSummary } from "./orderSnapshot.utils";

const POLL_MS = 3000;

/**
 * The snapshot pipeline's "3 · Import statuses" step: imports the day's built TOE, Toll and eStore
 * snapshots into the orders' statuses as a background job, showing (and polling) the day's latest run.
 */
export const OrderSnapshotStatusImport = () => {
  const snapshot = useSnapshot();
  const date = snapshot.date;
  const notify = snapshot.notify;
  const listArgs = { kind: ORDER_SNAPSHOT_STATUS_IMPORT_KIND, subject: date, limit: 1 };
  const latestQuery = getBackgroundJobList.useQuery(listArgs);
  const latest = latestQuery.data?.[0];
  const isActive = isBackgroundJobActive(latest);
  getBackgroundJobList.useQuerySubscription(listArgs, { pollingInterval: isActive ? POLL_MS : 0 });
  const [startJob, startState] = createBackgroundJob.useMutation();
  const isImporting = isActive || startState.isLoading;
  const previousStatus = useRef<BackgroundJobStatus | undefined>(latest?.status);

  const latestStatus = latest?.status;
  const latestError = latest?.error;
  useEffect(() => {
    const before = previousStatus.current;
    previousStatus.current = latestStatus;
    const wasActive = before === "QUEUED" || before === "RUNNING";
    if (!wasActive) return;
    if (latestStatus === "DONE") notify("Order statuses updated");
    else if (latestStatus === "FAILED") notify(`Status import failed: ${latestError ?? "unknown error"}`, "error");
  }, [latestStatus, latestError, notify]);

  const handleImport = async () => {
    try {
      await startJob({ kind: ORDER_SNAPSHOT_STATUS_IMPORT_KIND, input: { date } }).unwrap();
    }
    catch {
    }
  };

  let sub: ReactNode = "Never run";
  if (isImporting) {
    sub = "Updating order statuses…";
  }
  else if (startState.error) {
    sub = (
      <Box
        component="span"
        sx={{ color: "error.main" }}
      >
        Couldn't start: {toErrorText(startState.error)}
      </Box>
    );
  }
  else if (latest?.status === "DONE") {
    sub = (
      <Box
        component="span"
        sx={{
          alignItems: "center",
          color: "success.main",
          display: "inline-flex",
          gap: 0.5,
        }}
      >
        <CheckCircleOutlinedIcon sx={{ fontSize: 14 }} />
        Last run {formatDateTime(latest.finishedAt)} · {toOrderSnapshotStatusImportSummary(latest.result)}
      </Box>
    );
  }
  else if (latest?.status === "FAILED") {
    sub = (
      <Box
        component="span"
        sx={{
          alignItems: "center",
          color: "error.main",
          display: "inline-flex",
          gap: 0.5,
        }}
      >
        <ErrorOutlinedIcon sx={{ fontSize: 14 }} />
        Last run {formatDateTime(latest.finishedAt)} · failed: {latest.error ?? "unknown error"}
      </Box>
    );
  }

  let buttonIcon: ReactNode = <SyncIcon fontSize="small" />;
  let buttonLabel = "Import statuses";
  if (isImporting) {
    buttonIcon = (
      <CircularProgress
        color="inherit"
        size={14}
        thickness={6}
      />
    );
    buttonLabel = "Importing…";
  }

  return (
    <SnapshotPipelineStep
      action={(
        <Button
          disabled={isImporting}
          onClick={handleImport}
          size="small"
          startIcon={buttonIcon}
          sx={{ whiteSpace: "nowrap" }}
          variant="outlined"
        >
          {buttonLabel}
        </Button>
      )}
      label="3 · Import statuses"
      marker={<StorageOutlinedIcon sx={{ fontSize: 16 }} />}
      sub={sub}
    >
      {isImporting && (
        <LinearProgress
          sx={{
            borderRadius: 2,
            height: 4,
            mt: 1,
          }}
        />
      )}
    </SnapshotPipelineStep>
  );
};
